
import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Sparkles, Users, Star, Award, TrendingUp } from 'lucide-react';
import { Card3D, ScrollReveal, AnimatedText, MagneticButton } from '@/components/ui/premium';

export const PremiumBenefits = () => {
  const benefits = [
    {
      icon: Heart,
      title: "Cuidado Personalizado",
      description: "Uma jornada única, criada especialmente para você, respeitando seu ritmo e necessidades emocionais.",
      gradient: "from-pink-500 to-rose-500",
      stats: "97% satisfação"
    },
    {
      icon: Shield,
      title: "Privacidade Total",
      description: "Seus momentos íntimos protegidos com a mais alta tecnologia de segurança e confidencialidade.",
      gradient: "from-green-500 to-emerald-500",
      stats: "Criptografia militar"
    },
    {
      icon: Sparkles,
      title: "Crescimento Contínuo",
      description: "Evolua a cada dia com insights inteligentes que se adaptam ao seu progresso emocional.",
      gradient: "from-yellow-500 to-orange-500",
      stats: "+85% evolução"
    },
    {
      icon: Users,
      title: "Comunidade Acolhedora",
      description: "Conecte-se com pessoas que compartilham a mesma jornada de autoconhecimento e bem-estar.",
      gradient: "from-purple-500 to-indigo-500",
      stats: "50k+ membros"
    }
  ];

  const stats = [
    { number: "50mil+", label: "Pessoas transformadas", icon: Users },
    { number: "4.9", label: "Avaliação média", icon: Star },
    { number: "98%", label: "Recomendam YouCalm", icon: TrendingUp },
    { number: "150+", label: "Empresas parceiras", icon: Award }
  ];

  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/8 via-white/95 to-brand-primary/8">
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              'radial-gradient(circle at 0% 0%, rgba(97, 46, 215, 0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 100% 100%, rgba(132, 102, 218, 0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 50% 50%, rgba(214, 204, 242, 0.1) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <ScrollReveal animation="fade" delay={0.2}>
          <div className="text-center mb-20">
            <motion.div
              className="flex justify-center mb-8"
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full blur-xl opacity-30 scale-150"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                />
                <div className="relative w-20 h-20 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center shadow-2xl">
                  <Heart className="w-10 h-10 text-white" />
                </div>
              </div>
            </motion.div>

            <h2 className="text-5xl md:text-7xl font-bold mb-8">
              <AnimatedText
                text="Por que escolher"
                animation="gradient"
                className="block mb-4"
              />
              <AnimatedText
                text="o YouCalm?"
                animation="slide"
                delay={0.5}
                className="text-brand-primary"
              />
            </h2>

            <Card3D className="inline-block px-8 py-4 mb-8">
              <p className="text-xl text-brand-primary font-medium flex items-center gap-2">
                <Heart className="w-6 h-6" />
                Mais do que uma plataforma, somos seu companheiro de jornada
              </p>
            </Card3D>
          </div>
        </ScrollReveal>

        {/* Benefits Grid */}
        <ScrollReveal animation="fade" delay={0.4}>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, rotateY: -30 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  delay: 0.6 + index * 0.1,
                  duration: 0.8,
                  type: 'spring',
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -20, 
                  rotateY: 10,
                  transition: { duration: 0.3 }
                }}
                className="group"
              >
                <Card3D className="p-8 h-full relative overflow-hidden" intensity="medium">
                  {/* Background pattern */}
                  <motion.div
                    className="absolute top-0 right-0 opacity-5"
                    animate={{
                      rotate: [0, 360],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 10, repeat: Infinity }}
                  >
                    <benefit.icon className="w-32 h-32 text-brand-primary" />
                  </motion.div>

                  <div className="relative z-10">
                    {/* Icon */}
                    <motion.div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-6 shadow-xl`}
                      whileHover={{ 
                        scale: 1.1, 
                        rotate: 360,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                      }}
                      transition={{ duration: 0.6 }}
                    >
                      <benefit.icon className="w-8 h-8 text-white" />
                    </motion.div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-4 text-brand-primary group-hover:text-brand-secondary transition-colors duration-300">
                      {benefit.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {benefit.description}
                    </p>

                    {/* Stats */}
                    <div className="text-sm font-bold text-brand-primary bg-brand-accent/20 px-3 py-2 rounded-full inline-block">
                      {benefit.stats}
                    </div>
                  </div>

                  {/* Hover effect */}
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-3xl`}
                  />
                </Card3D>
              </motion.div>
            ))}
          </div>
        </ScrollReveal>

        {/* Stats Section */}
        <ScrollReveal animation="scale" delay={0.8}>
          <Card3D className="p-12 bg-gradient-to-br from-white/90 to-brand-accent/10" intensity="strong">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-brand-primary mb-4">
                Confiança que transforma vidas
              </h3>
              <p className="text-xl text-gray-600">
                Milhares de pessoas já descobriram o poder do autocuidado emocional
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center group"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: 1 + index * 0.1,
                    duration: 0.6,
                    type: 'spring',
                    stiffness: 200
                  }}
                  whileHover={{ scale: 1.1 }}
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <stat.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <motion.div
                    className="text-4xl md:text-5xl font-bold text-brand-primary mb-2"
                    animate={{
                      scale: [1, 1.05, 1]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.5
                    }}
                  >
                    {stat.number}
                  </motion.div>
                  
                  <div className="text-gray-600 font-medium">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </Card3D>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal animation="fade" delay={1.2}>
          <div className="text-center mt-20">
            <MagneticButton
              size="lg"
              variant="primary"
              className="shadow-2xl shadow-brand-primary/30"
            >
              <Heart className="w-6 h-6" />
              Começar minha transformação
            </MagneticButton>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PremiumBenefits;
